import { acquireRelease, uninterruptibleMask, zipRight, ensuring } from '../../Effect/dist/effect-Effect.esm.js';
import { equals } from '../../Equal/dist/effect-Equal.esm.js';
import { threadName } from '../../FiberId/dist/effect-FiberId.esm.js';
import { dual } from '../../Function/dist/effect-Function.esm.js';
import { make as make$2, isEmpty, size, has, get as get$1, empty, remove, set } from '../../HashMap/dist/effect-HashMap.esm.js';
import { withSTMRuntime, succeed, retry, effect, zipWith, map, commit } from './core.esm.js';
import { unsafeGet, unsafeSet, make as make$1, get } from './tRef.esm.js';
import { getOrElse } from '../../Option/dist/effect-Option.esm.js';

const TReentrantLockSymbolKey = "effect/TReentrantLock";

/** @internal */
const TReentrantLockTypeId = /*#__PURE__*/Symbol.for(TReentrantLockSymbolKey);
const WriteLockTypeId = /*#__PURE__*/Symbol.for("effect/TReentrantLock/WriteLock");
const ReadLockTypeId = /*#__PURE__*/Symbol.for("effect/TReentrantLock/ReadLock");
class TReentranLockImpl {
  [TReentrantLockTypeId] = TReentrantLockTypeId;
  constructor(state) {
    this.state = state;
  }
}

/** @internal */

/**
 * This data structure describes the state of the lock when multiple fibers
 * have acquired read locks. The state is tracked as a map from fiber identity
 * to number of read locks acquired by the fiber. This level of detail permits
 * upgrading a read lock to a write lock.
 *
 * @internal
 */
class ReadLock {
  [ReadLockTypeId] = ReadLockTypeId;
  constructor(readers) {
    this.readers = readers;
  }
  get readLocks() {
    return Array.from(this.readers).reduce((acc, curr) => acc + curr[1], 0);
  }
  get writeLocks() {
    return 0;
  }
  readLocksHeld(fiberId) {
    return getOrElse(get$1(this.readers, fiberId), () => 0);
  }
  writeLocksHeld(_fiberId) {
    return 0;
  }
}

/**
 * This data structure describes the state of the lock when a single fiber has
 * a write lock. The fiber has an identity, and may also have acquired a
 * certain number of read locks.
 *
 * @internal
 */
class WriteLock {
  [WriteLockTypeId] = WriteLockTypeId;
  constructor(readLocks, writeLocks, fiberId) {
    this.readLocks = readLocks;
    this.writeLocks = writeLocks;
    this.fiberId = fiberId;
  }
  readLocksHeld(fiberId) {
    return equals(fiberId)(this.fiberId) ? this.readLocks : 0;
  }
  writeLocksHeld(fiberId) {
    return equals(fiberId)(this.fiberId) ? this.writeLocks : 0;
  }
}
const isReadLock = lock => {
  return ReadLockTypeId in lock;
};
const isWriteLock = lock => {
  return WriteLockTypeId in lock;
};

/**
 * An empty read lock state, in which no fiber holds any read locks.
 */
const emptyReadLock = /*#__PURE__*/new ReadLock( /*#__PURE__*/empty());

/**
 * Creates a new read lock where the specified fiber holds the specified
 * number of read locks.
 */
const makeReadLock = (fiberId, count) => {
  if (count <= 0) {
    return emptyReadLock;
  }
  return new ReadLock(make$2([fiberId, count]));
};

/**
 * Determines if there is no other holder of read locks aside from the
 * specified fiber id. If there are no other holders of read locks aside
 * from the specified fiber id, then it is safe to upgrade the read lock
 * into a write lock.
 */
const noOtherHolder = (readLock, fiberId) => {
  return isEmpty(readLock.readers) || size(readLock.readers) === 1 && has(readLock.readers, fiberId);
};

/**
 * Adjusts the number of read locks held by the specified fiber id.
 */
const adjustReadLock = (readLock, fiberId, adjustment) => {
  const total = readLock.readLocksHeld(fiberId);
  const newTotal = total + adjustment;
  if (newTotal < 0) {
    throw new Error("BUG - TReentrantLock.ReadLock.adjust - please report an issue at https://github.com/Effect-TS/stm/issues");
  }
  if (newTotal === 0) {
    return new ReadLock(remove(readLock.readers, fiberId));
  }
  return new ReadLock(set(readLock.readers, fiberId, newTotal));
};
const adjustRead = (self, delta) => withSTMRuntime(runtime => {
  const lock = unsafeGet(self.state, runtime.journal);
  if (isReadLock(lock)) {
    const result = adjustReadLock(lock, runtime.fiberId, delta);
    unsafeSet(self.state, result, runtime.journal);
    return succeed(result.readLocksHeld(runtime.fiberId));
  }
  if (isWriteLock(lock) && equals(runtime.fiberId)(lock.fiberId)) {
    const newTotal = lock.readLocks + delta;
    if (newTotal < 0) {
      throw new Error(`Defect: Fiber ${threadName(runtime.fiberId)} releasing read locks it does not hold, newTotal: ${newTotal}`);
    }
    unsafeSet(self.state, new WriteLock(newTotal, lock.writeLocks, runtime.fiberId), runtime.journal);
    return succeed(newTotal);
  }
  return retry;
});

/** @internal */
const acquireRead = self => adjustRead(self, 1);

/** @internal */
const acquireWrite = self => withSTMRuntime(runtime => {
  const lock = unsafeGet(self.state, runtime.journal);
  if (isReadLock(lock) && noOtherHolder(lock, runtime.fiberId)) {
    unsafeSet(self.state, new WriteLock(lock.readLocksHeld(runtime.fiberId), 1, runtime.fiberId), runtime.journal);
    return succeed(1);
  }
  if (isWriteLock(lock) && equals(runtime.fiberId)(lock.fiberId)) {
    unsafeSet(self.state, new WriteLock(lock.readLocks, lock.writeLocks + 1, runtime.fiberId), runtime.journal);
    return succeed(lock.writeLocks + 1);
  }
  return retry;
});

/** @internal */
const fiberReadLocks = self => effect((journal, fiberId) => unsafeGet(self.state, journal).readLocksHeld(fiberId));

/** @internal */
const fiberWriteLocks = self => effect((journal, fiberId) => unsafeGet(self.state, journal).writeLocksHeld(fiberId));

/** @internal */
const lock = self => writeLock(self);

/** @internal */
const locked = self => zipWith(readLocked(self), writeLocked(self), (x, y) => x || y);

/** @internal */
const make = /*#__PURE__*/map( /*#__PURE__*/make$1(emptyReadLock), readLock => new TReentranLockImpl(readLock));

/** @internal */
const readLock = self => acquireRelease(commit(acquireRead(self)), () => commit(releaseRead(self)));

/** @internal */
const readLocks = self => map(get(self.state), state => state.readLocks);

/** @internal */
const readLocked = self => map(get(self.state), state => state.readLocks > 0);

/** @internal */
const releaseRead = self => adjustRead(self, -1);

/** @internal */
const releaseWrite = self => withSTMRuntime(runtime => {
  const lock = unsafeGet(self.state, runtime.journal);
  if (isWriteLock(lock) && lock.writeLocks === 1 && equals(runtime.fiberId)(lock.fiberId)) {
    const result = makeReadLock(lock.fiberId, lock.readLocks);
    unsafeSet(self.state, result, runtime.journal);
    return succeed(result.writeLocksHeld(runtime.fiberId));
  }
  if (isWriteLock(lock) && equals(runtime.fiberId)(lock.fiberId)) {
    const result = new WriteLock(lock.readLocks, lock.writeLocks - 1, runtime.fiberId);
    unsafeSet(self.state, result, runtime.journal);
    return succeed(result.writeLocksHeld(runtime.fiberId));
  }
  throw new Error(`Defect: Fiber ${threadName(runtime.fiberId)} releasing write lock it does not hold`);
});

/** @internal */
const withLock = /*#__PURE__*/dual(2, (effect, self) => withWriteLock(effect, self));

/** @internal */
const withReadLock = /*#__PURE__*/dual(2, (effect, self) => uninterruptibleMask(restore => zipRight(restore(commit(acquireRead(self))), ensuring(effect, commit(releaseRead(self))))));

/** @internal */
const withWriteLock = /*#__PURE__*/dual(2, (effect, self) => uninterruptibleMask(restore => zipRight(restore(commit(acquireWrite(self))), ensuring(effect, commit(releaseWrite(self))))));

/** @internal */
const writeLock = self => acquireRelease(commit(acquireWrite(self)), () => commit(releaseWrite(self)));

/** @internal */
const writeLocked = self => map(get(self.state), state => state.writeLocks > 0);

/** @internal */
const writeLocks = self => map(get(self.state), state => state.writeLocks);

export { ReadLock, TReentrantLockTypeId, WriteLock, acquireRead, acquireWrite, fiberReadLocks, fiberWriteLocks, lock, locked, make, readLock, readLocked, readLocks, releaseRead, releaseWrite, withLock, withReadLock, withWriteLock, writeLock, writeLocked, writeLocks };
